/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState, useCallback } from 'react';
import { CheckIcon } from '@/shared/ui/radix-icons';
import { Text, Button, Image } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

/**
 * Compact metric cell for area / bedrooms / bathrooms.
 */
export function StatCell({ value, unit, label }: { value: string; unit: string; label: string }) {
  return (
    <div className="border-b border-(--border-subtle) py-(--space-2) text-center">
      <Text as="span" className="text-[length:var(--text-app-heading)] font-bold text-(--text-primary)">
        {value}
      </Text>
      {unit && (
        <Text as="span" className="ml-(--space-0-5) text-[length:var(--text-body-sm)] text-(--text-tertiary)">
          {unit}
        </Text>
      )}
      <Text className="mt-(--space-0-5) text-[length:var(--text-meta)] text-(--text-tertiary)">{label}</Text>
    </div>
  );
}

/**
 * Amenity / security chip matching property-detail CharacteristicsSections.
 */
export function Chip({ label }: { label: string }) {
  return (
    <Text
      as="span"
      className={cn(
        'inline-flex items-center gap-(--space-1)',
        'rounded-(--radius-md) border border-[color-mix(in_srgb,var(--seed-base)_15%,transparent)]',
        'bg-[color-mix(in_srgb,var(--seed-base)_6%,var(--surface-primary))]',
        'px-(--space-3) py-(--space-1) text-[length:var(--text-meta)] text-(--text-primary)',
      )}
    >
      <CheckIcon className="size-[10px] text-(--status-success)" />
      {label}
    </Text>
  );
}

/**
 * Full-screen image lightbox with keyboard navigation.
 */
export function Lightbox({
  images,
  startIndex,
  onClose,
}: {
  images: string[];
  startIndex: number;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(startIndex);
  const [scale, setScale] = useState(1);

  const prev = useCallback(() => {
    setScale(1);
    setIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const next = useCallback(() => {
    setScale(1);
    setIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, prev, next]);

  const navBtn =
    'absolute top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-(--overlay-scrim) text-[length:var(--text-app-heading)] text-(--text-inverse)';

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-(--overlay-scrim)"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <Button
        variant="none"
        disableSize
        onClick={onClose}
        className="absolute top-(--space-4) right-(--space-4) p-(--space-2) text-(--text-3xl) leading-none text-(--text-inverse)"
      >
        ✕
      </Button>
      <Text
        as="span"
        className="absolute top-(--space-5) left-1/2 -translate-x-1/2 text-[length:var(--text-body-sm)] font-medium text-(--text-inverse)"
      >
        {index + 1} / {images.length}
      </Text>
      <Button
        variant="none"
        disableSize
        onClick={(e) => {
          e.stopPropagation();
          prev();
        }}
        className={cn(navBtn, 'left-(--space-4)')}
      >
        ‹
      </Button>
      <div
        onClick={(e) => {
          e.stopPropagation();
          setScale((s) => (s === 1 ? 2 : 1));
        }}
        className={cn('max-h-[85vh] max-w-[85vw] overflow-hidden', scale === 1 ? 'cursor-zoom-in' : 'cursor-zoom-out')}
      >
        <Image
          src={images[index]}
          alt=""
          className="max-h-[85vh] max-w-[85vw] object-contain transition-transform duration-(--transition-standard)"
          style={{ transform: `scale(${scale})` }}
        />
      </div>
      <Button
        variant="none"
        disableSize
        onClick={(e) => {
          e.stopPropagation();
          next();
        }}
        className={cn(navBtn, 'right-(--space-4)')}
      >
        ›
      </Button>
    </div>
  );
}
